// src/stores/postStore.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import PostService from "@/services/post-service";
import PostAttachmentService from "@/services/postAttachment-service";
import Swal from "sweetalert2";

export const usePostStore = defineStore("post", () => {
  // State
  const posts = ref([]);
  const currentPost = ref(null);
  const loading = ref(false);
  const loadingComments = ref(false);
  const loadingAttachments = ref(false);

  // Getters
  const publishedPosts = computed(() => {
    return posts.value.filter((post) => post.date !== null);
  });

  const draftPosts = computed(() => {
    return posts.value.filter((post) => post.date === null);
  });

  const getMediaTypeLabel = (type) => {
    const mediaType = mediaTypes.value.find((m) => m.value === type);
    return mediaType ? mediaType.label : type;
  };

  const getShareTypeLabel = (type) => {
    const shareType = shareTypes.value.find((s) => s.value === type);
    return shareType ? shareType.label : type;
  };

  const getPostById = (id) => {
    return posts.value.find((post) => post.id === id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not published";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatErrors = (errors) => {
    if (!errors)
      return '<div style="text-align: center; color: #666;">An error occurred</div>';

    const allMessages = [];

    // Récupérer tous les messages d'erreur
    Object.values(errors).forEach((messages) => {
      if (Array.isArray(messages)) {
        allMessages.push(...messages);
      } else {
        allMessages.push(messages);
      }
    });

    // Si un seul message
    if (allMessages.length === 1) {
      return `<div style="text-align: center; color: #666;">${allMessages[0]}</div>`;
    }

    // Si plusieurs messages avec des points
    return `
    <div style="text-align: center;">
      ${allMessages
        .map((msg) => `<div class="mb-1" style="color: #666;">• ${msg}</div>`)
        .join("")}
    </div>
  `;
  };

  // Actions
  //linkedin
  //  const createPost2 = async (formData) => {
  //   try {
  //     const response = await api.post('/posts', formData, {
  //       headers: {
  //         'Content-Type': 'multipart/form-data'
  //       }
  //     })
  //     return response.data
  //   } catch (error) {
  //     throw error
  //   }
  // };

  // const addComment2 = async (postId, commentData) => {
  //   try {
  //     const response = await api.post(`/posts/comments/${postId}`, commentData)
  //     return response.data
  //   } catch (error) {
  //     throw error
  //   }
  // };

  // const deleteComment2 = async (commentId) => {
  //   try {
  //     await api.delete(`/posts/comments/${commentId}`)
  //   } catch (error) {
  //     throw error
  //   }
  // };

  // const publishPost2 = async (postId) => {
  //   try {
  //     await api.patch(`/posts/${postId}`, { published: 'Y' })
  //   } catch (error) {
  //     throw error
  //   }
  // };

  const fetchPosts = async () => {
    try {
      loading.value = true;
      const response = await PostService.getPosts();
      posts.value = response.data?.data || [];
      return posts.value;
    } catch (error) {
      console.error("Error fetching posts:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load posts",
      });
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const fetchPostById = async (id) => {
    try {
      loading.value = true;
      const response = await PostService.getPostById(id);
      currentPost.value = response.data || null;
      return currentPost.value;
    } catch (error) {
      console.error("Error fetching post:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load post details",
      });
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const createPost = async (postData) => {
    try {
      loading.value = true;

      // Si un fichier est inclus, utiliser FormData
      let formData = new FormData();
      let hasFile = false;

      // Ajouter les champs de base
      const postFields = [
        "title",
        "description",
        "date",
        "media_type",
        "media_content",
        "share_type",
        "share_id",
        "employee",
      ];
      postFields.forEach((field) => {
        if (postData[field] !== undefined && postData[field] !== null) {
          formData.append(field, postData[field]);
        }
      });

      // Ajouter le fichier s'il existe
      if (postData.file && postData.file instanceof File) {
        formData.append("file", postData.file);
        hasFile = true;
      }

      let response;
      if (hasFile) {
        // Utiliser FormData pour les uploads de fichiers
        response = await PostService.createPost(formData);
      } else {
        // Utiliser JSON pour les posts sans fichiers
        response = await PostService.createPost(postData);
      }

      const newPost = response.data?.data || response.data;
      posts.value.push(newPost);

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Post created successfully",
      });

      return newPost;
    } catch (error) {
      console.error("Error creating post:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to create post",
      });
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const updatePost = async (id, postData) => {
    try {
      loading.value = true;

      console.log("les donnees a mettre a jour du post:", postData);
      // Si un fichier est inclus, utiliser FormData
      let formData = new FormData();
      let hasFile = false;

      // Ajouter les champs de base
      const postFields = [
        "title",
        "description",
        "date",
        "media_type",
        "media_content",
        "share_type",
        "share_id",
        "employee",
      ];
      postFields.forEach((field) => {
        if (postData[field] !== undefined && postData[field] !== null) {
          formData.append(field, postData[field]);
        }
      });

      // Ajouter le fichier s'il existe
      if (postData.file && postData.file instanceof File) {
        formData.append("file", postData.file);
        hasFile = true;
      }

      let response;
      let updatedPost;
      if (hasFile) {
        // Utiliser FormData pour les uploads de fichiers
        response = await PostService.updatePost(id, formData);
        updatedPost = response.data?.data || response.data;
      } else {
        // Utiliser JSON pour les posts sans fichiers
        response = await PostService.updatePost(id, postData);
        updatedPost = response.data?.data || response.data;
      }
      // const response = await PostService.updatePost(id, postData);
      // const updatedPost = response.data?.data || response.data;

      // Mettre à jour dans la liste
      const index = posts.value.findIndex((post) => post.id === id);
      if (index !== -1) {
        posts.value[index] = updatedPost;
      }

      // Mettre à jour le post courant s'il est actif
      if (currentPost.value && currentPost.value.id === id) {
        currentPost.value = updatedPost;
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Post updated successfully",
      });

      return updatedPost;
    } catch (error) {
      console.error("Error updating post:", error);
      Swal.fire({
        icon: "error",
        title: error.response?.data?.message || "Failed to update post",
        text: error.response?.data?.errors || "Failed to update post",
      });
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const publishPost = async (id) => {
    try {
      loading.value = true;
      await PostService.publishPost(id);

      // Mettre à jour dans la liste
      const index = posts.value.findIndex((post) => post.id === id);
      if (index !== -1) {
        posts.value[index].date = new Date().toISOString();
      }

      // Mettre à jour le post courant s'il est actif
      if (currentPost.value && currentPost.value.id === id) {
        currentPost.value.date = new Date().toISOString();
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Post published successfully",
      });

      return true;
    } catch (error) {
      console.error("Error publishing post:", error);

      let errorMessage = "Failed to publish post";
      if (error.response?.status === 400) {
        errorMessage = "The post is already published";
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const deletePost = async (id) => {
    try {
      loading.value = true;
      await PostService.deletePost(id);

      // Supprimer de la liste
      posts.value = posts.value.filter((post) => post.id !== id);

      // Si c'est le post courant, le vider
      if (currentPost.value && currentPost.value.id === id) {
        currentPost.value = null;
      }

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Post has been deleted",
      });

      return true;
    } catch (error) {
      console.error("Error deleting post:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete post",
      });
      throw error;
    } finally {
      loading.value = false;
    }
  };

  // Gestion des commentaires
  const addComment = async (postId, commentData) => {
    try {
      loadingComments.value = true;
      const response = await PostService.addPostcomment(postId, commentData);
      const newComment = response.data?.data || response.data;

      // Ajouter au post courant si c'est le bon post
      if (currentPost.value && currentPost.value.id === postId) {
        if (!currentPost.value.comments) {
          currentPost.value.comments = [];
        }
        currentPost.value.comments.push(newComment);
      }

      // Ajouter à la liste des posts si nécessaire
      const postIndex = posts.value.findIndex((post) => post.id === postId);
      if (postIndex !== -1) {
        if (!posts.value[postIndex].comments) {
          posts.value[postIndex].comments = [];
        }
        posts.value[postIndex].comments.push(newComment);
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Comment added successfully",
      });

      return newComment;
    } catch (error) {
      console.error("Error adding comment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to add comment",
      });
      throw error;
    } finally {
      loadingComments.value = false;
    }
  };

  const editComment = async (commentId, commentData) => {
    try {
      loadingComments.value = true;
      const response = await PostService.editPostcomment(
        commentId,
        commentData,
      );
      const updatedComment = response.data?.data || response.data;

      // Mettre à jour dans le post courant
      if (currentPost.value && currentPost.value.comments) {
        const commentIndex = currentPost.value.comments.findIndex(
          (c) => c.id === commentId,
        );
        if (commentIndex !== -1) {
          currentPost.value.comments[commentIndex] = updatedComment;
        }
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Comment updated successfully",
      });

      return updatedComment;
    } catch (error) {
      console.error("Error editing comment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update comment",
      });
      throw error;
    } finally {
      loadingComments.value = false;
    }
  };

  const deleteComment = async (commentId) => {
    try {
      loadingComments.value = true;
      await PostService.deletePostcomment(commentId);

      // Supprimer du post courant
      if (currentPost.value && currentPost.value.comments) {
        currentPost.value.comments = currentPost.value.comments.filter(
          (c) => c.id !== commentId,
        );
      }

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Comment has been deleted",
      });

      return true;
    } catch (error) {
      console.error("Error deleting comment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete comment",
      });
      throw error;
    } finally {
      loadingComments.value = false;
    }
  };

  // Gestion des pièces jointes
  const uploadAttachment = async (postId, attachmentData) => {
    try {
      loadingAttachments.value = true;
      const response = await PostAttachmentService.uploadPostAttachment(
        postId,
        attachmentData,
      );
      const newAttachment = response.data?.data || response.data;

      // Ajouter au post courant si c'est le bon post
      if (currentPost.value && currentPost.value.id === postId) {
        if (!currentPost.value.attachments) {
          currentPost.value.attachments = [];
        }
        currentPost.value.attachments.push(newAttachment);
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Attachment uploaded successfully",
      });

      return newAttachment;
    } catch (error) {
      console.error("Error uploading attachment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to upload attachment",
      });
      throw error;
    } finally {
      loadingAttachments.value = false;
    }
  };

  const deleteAttachment = async (attachmentId) => {
    try {
      loadingAttachments.value = true;
      await PostAttachmentService.deletePostAttachment(attachmentId);

      // Supprimer du post courant
      if (currentPost.value && currentPost.value.attachments) {
        currentPost.value.attachments = currentPost.value.attachments.filter(
          (a) => a.id !== attachmentId,
        );
      }

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Attachment has been deleted",
      });

      return true;
    } catch (error) {
      console.error("Error deleting attachment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete attachment",
      });
      throw error;
    } finally {
      loadingAttachments.value = false;
    }
  };

  // Options disponibles
  const mediaTypes = computed(() => [
    {
      value: "T",
      label: "Text",
      icon: "ri-file-text-line",
      color: "secondary",
    },
    { value: "V", label: "Video", icon: "ri-video-line", color: "danger" },
    { value: "A", label: "Audio", icon: "ri-music-line", color: "success" },
    { value: "I", label: "Image", icon: "ri-image-line", color: "info" },
    { value: "L", label: "Link", icon: "ri-link", color: "primary" },
    { value: "F", label: "File", icon: "ri-file-text-line", color: "warning" },
  ]);

  const shareTypes = computed(() => [
    { value: "D", label: "Department" },
    { value: "S", label: "Structure" },
    { value: "M", label: "Structure Manager" },
    { value: "J", label: "Job" },
    { value: "G", label: "Group" },
  ]);

  // const mediaTypes = computed(() => [
  //   { value: 'I', label: 'Image', icon: 'ri-image-line' },
  //   { value: 'L', label: 'Link', icon: 'ri-link' },
  //   { value: 'V', label: 'Video', icon: 'ri-video-line' },
  //   { value: 'A', label: 'Audio', icon: 'ri-music-line' },
  //   { value: 'T', label: 'Text', icon: 'ri-file-text-line' },
  //   { value: 'F', label: 'File', icon: 'ri-file-text-line' }
  // ]);

  // const shareTypes = computed(() => [
  //   { value: 'D', label: 'Department' },
  //   { value: 'S', label: 'Structure' },
  //   { value: 'M', label: 'Structure Manager' },
  //   { value: 'J', label: 'Job' },
  //   { value: 'G', label: 'Group' }
  // ]);

  // Réinitialiser
  const resetCurrentPost = () => {
    currentPost.value = null;
  };

  return {
    // State
    posts,
    currentPost,
    loading,
    loadingComments,
    loadingAttachments,
    mediaTypes,
    shareTypes,

    // Getters
    publishedPosts,
    draftPosts,
    getMediaTypeLabel,
    getShareTypeLabel,
    getPostById,
    formatDate,
    formatErrors,

    // Actions
    fetchPosts,
    fetchPostById,
    createPost,
    updatePost,
    publishPost,
    deletePost,
    addComment,
    editComment,
    deleteComment,
    // addComment2,
    // deleteComment2,
    // publishPost2,
    // createPost2,
    uploadAttachment,
    deleteAttachment,
    resetCurrentPost,
  };
});
